import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";
import { ArrowLeft, MapPin, Package, ChevronRight, Truck, X } from "lucide-react";
import { toast } from "react-toastify";

import "../styles/scansheet.css";
import "../styles/book-in.css";
import "../styles/move.css";
import { useTranslation } from "../i18n";
import { logoutProcessor } from "../components/helpers/logoutProcessor";
import {
    playSuccess,
    playError,
    playBoxAdd,
    playComplete,
    playToteFinalised,
} from "../components/helpers/audioPlayer";
import HiddenScanInput from "../components/HiddenScanInput";
import FitWord from "../components/FitWord";
import ScanStatusIcons from "../components/ScanStatusIcons";
import ScanFlashOverlay from "../components/ScanFlashOverlay";
import FlowLoader from "../components/FlowLoader";
import { useDemoVisible } from "../components/helpers/demoMode";
import {
    preventScanPageButtonFocus,
    blurClickedScanPageButton,
} from "../components/helpers/scanFocusGuard";
import { apiFetch } from "../api/apiFetch";

const TOAST_ID = {
    nothingScanned: "mv-nothing-scanned",
    palletUnknown: "mv-pallet-unknown",
    palletWrongWarehouse: "mv-pallet-wrong-warehouse",
    palletAlreadyProcessed: "mv-pallet-already-processed",
    palletNotPlaced: "mv-pallet-not-placed",
    invalidLocation: "mv-invalid-location",
    inboundOnly: "mv-inbound-only",
    serverError: "mv-server-error",
    noInboundLocation: "mv-no-inbound-location",
} as const;

type Phase =
    | "suggest-bay"
    | "awaiting-pallet"
    | "awaiting-scansheet"
    | "complete";

interface PendingPallet {
    pallet_id: string;
    daily_seq: number;
    created_at: string;
    // Inbound stock type chosen when the label was printed (Goods In
    // settings); null for labels printed before types were set up.
    stock_type?: string | null;
}

interface Bay {
    location_id: string;
    display_name: string | null;
    oldest_created_at: string;
    count: number;
    pallets: PendingPallet[];
}

const Move = () => {
    usePageTitle("move");
    const navigate = useNavigate();
    const { t } = useTranslation();
    const demo = useDemoVisible();

    const userData = window.localStorage.getItem("user");
    const userObject =
        userData !== null ? JSON.parse(userData) : { uid: 0, name: "", permissions: "", warehouse: "Unknown" };

    const [phase, setPhase] = useState<Phase>("suggest-bay");
    const [bays, setBays] = useState<Bay[]>([]);
    const [activeBay, setActiveBay] = useState<Bay | null>(null);
    const [currentPallet, setCurrentPallet] = useState<string | null>(null);
    const [inboundBarcode, setInboundBarcode] = useState<string | null>(null);
    const [scanValue, setScanValue] = useState("");
    const [loading, setLoading] = useState(false);
    const [scanStatus, setScanStatus] = useState<"idle" | "success" | "error" | "complete">("idle");
    const [initialised, setInitialised] = useState(false);
    // Override modal — opened by the "Choose lane" button when there's
    // inbound stock in more than one lane. Defaults closed; the suggested
    // (oldest-stock) lane is the FIFO recommendation.
    const [chooseLaneOpen, setChooseLaneOpen] = useState(false);

    const phaseRef = useRef(phase);
    const currentPalletRef = useRef(currentPallet);
    useEffect(() => { phaseRef.current = phase; }, [phase]);
    useEffect(() => { currentPalletRef.current = currentPallet; }, [currentPallet]);

    const statusTimerRef = useRef<NodeJS.Timeout | null>(null);
    const abortRef = useRef<AbortController | null>(null);
    const mountedRef = useRef(true);
    useEffect(() => () => {
        mountedRef.current = false;
        abortRef.current?.abort();
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
    }, []);

    const getSignal = () => {
        abortRef.current?.abort();
        const ctrl = new AbortController();
        abortRef.current = ctrl;
        return ctrl.signal;
    };

    const flashStatus = (status: "success" | "error" | "complete") => {
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
        setScanStatus(status);
        statusTimerRef.current = setTimeout(() => setScanStatus("idle"), 800);
    };

    // Fetch the queue + the warehouse's inbound-processing location. Set
    // initialised once both resolve so the page never flashes a loading
    // state on top of phase content.
    const refreshQueue = useCallback(async (markInitialised: boolean) => {
        const wh = userObject.warehouse;
        if (!wh) {
            if (markInitialised) setInitialised(true);
            return;
        }
        try {
            const [queueRes, locRes] = await Promise.all([
                apiFetch(`/pallet-receiving/move/queue?warehouse=${encodeURIComponent(wh)}`, {
                }),
                apiFetch(`/pallet-receiving/inbound-location?warehouse=${encodeURIComponent(wh)}`, {
                }),
            ]);
            if (!mountedRef.current) return;
            if (queueRes.ok) {
                const data = await queueRes.json();
                const list: Bay[] = Array.isArray(data.bays) ? data.bays : [];
                setBays(list);
                if (markInitialised) {
                    if (list.length === 0) {
                        setPhase("complete");
                    } else {
                        setActiveBay(list[0]);
                        setPhase("suggest-bay");
                    }
                }
            }
            if (locRes.ok) {
                const data = await locRes.json();
                setInboundBarcode(data.exists ? data.barcode : null);
            }
        } catch {
            // Stay at default state.
        } finally {
            if (mountedRef.current && markInitialised) setInitialised(true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userObject.warehouse]);

    useEffect(() => {
        refreshQueue(true);
    }, [refreshQueue]);

    const checkPallet = async (palletId: string) => {
        const res = await apiFetch(`/pallet-receiving/move/scan-pallet`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                palletId,
                warehouse: userObject.warehouse,
                uid: userObject.uid,
                name: userObject.name,
            }),
            signal: getSignal(),
        });
        if (res.status === 401) {
            logoutProcessor();
            navigate("/login");
            return { valid: false } as { valid: boolean; reason?: string };
        }
        if (!res.ok) return { valid: false };
        return await res.json();
    };

    const deliverPallet = async (palletId: string, locationId: string) => {
        const res = await apiFetch(`/pallet-receiving/move/deliver`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                palletId,
                locationId,
                uid: userObject.uid,
                name: userObject.name,
                warehouse: userObject.warehouse,
            }),
            signal: getSignal(),
        });
        if (res.status === 401) {
            logoutProcessor();
            navigate("/login");
            return null;
        }
        const data = await res.json().catch(() => null);
        if (!res.ok) {
            return { success: false, reason: data?.reason ?? "server_error" } as { success: false; reason: string };
        }
        return data as {
            success: true;
            // 'processed' = scan-sheet area, pallet leaves the queue;
            // 'stored' = re-homed to a warehouse location, still queued.
            action?: "processed" | "stored";
            location?: { barcode: string; display_name: string | null; type: string };
            pendingMove: number;
        };
    };

    const doSubmit = async (scannedValue?: string) => {
        const val = (scannedValue ?? scanValue).trim();
        if (!val) {
            toast.error(t("nothing_scanned"), { toastId: TOAST_ID.nothingScanned });
            return;
        }
        setLoading(true);
        try {
            const ph = phaseRef.current;

            if (ph === "awaiting-pallet" || ph === "suggest-bay") {
                const result = await checkPallet(val);
                if (!result.valid) {
                    playError();
                    flashStatus("error");
                    const reasonMap: Record<string, [string, string]> = {
                        pallet_wrong_warehouse: [t("pallet_wrong_warehouse"), TOAST_ID.palletWrongWarehouse],
                        pallet_already_processed: [t("pallet_already_processed"), TOAST_ID.palletAlreadyProcessed],
                        pallet_unknown: [t("pallet_unknown"), TOAST_ID.palletUnknown],
                    };
                    const [msg, id] = result.reason && reasonMap[result.reason]
                        ? reasonMap[result.reason]
                        : [t("pallet_not_placed"), TOAST_ID.palletNotPlaced];
                    toast.error(msg, { toastId: id });
                    return;
                }
                setCurrentPallet(val);
                setPhase("awaiting-scansheet");
                playBoxAdd();
                flashStatus("success");
                // A stock check flagged this label missing — the scan in
                // hand clears that on delivery, so just let the driver know.
                if (result.missing_at) {
                    toast.info(t("move_pallet_was_missing"), { toastId: "mv-was-missing" });
                }
                return;
            }

            if (ph === "awaiting-scansheet") {
                // Rescanning the same pallet cancels.
                if (val === currentPalletRef.current) {
                    setCurrentPallet(null);
                    setPhase("awaiting-pallet");
                    playBoxAdd();
                    flashStatus("success");
                    return;
                }

                const placed = await deliverPallet(currentPalletRef.current!, val);
                if (!placed) {
                    playError();
                    flashStatus("error");
                    toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
                    return;
                }
                if (!placed.success) {
                    playError();
                    flashStatus("error");
                    if (placed.reason === "wrong_type") {
                        toast.error(t("move_inbound_processing_only"), { toastId: TOAST_ID.inboundOnly });
                    } else {
                        toast.error(t("invalid_location"), { toastId: TOAST_ID.invalidLocation });
                    }
                    return;
                }
                if (placed.action === "stored") {
                    // Re-homed to a warehouse location: it stays in the
                    // queue, so this is an ordinary success, not a finish.
                    playSuccess();
                    flashStatus("success");
                    setCurrentPallet(null);
                    toast.info(
                        t("move_stored_at", { location: placed.location?.display_name || placed.location?.barcode || val }),
                        { toastId: "mv-stored" },
                    );
                    await refreshQueue(false);
                    setPhase("suggest-bay");
                    return;
                }
                playToteFinalised();
                flashStatus("complete");
                setCurrentPallet(null);
                if (placed.pendingMove === 0) {
                    playComplete();
                    setPhase("complete");
                    setBays([]);
                } else {
                    // Refresh the queue so the suggestion reflects the latest state.
                    await refreshQueue(false);
                    setPhase("suggest-bay");
                }
                return;
            }
        } catch (err) {
            if ((err as any)?.name === "AbortError" || !mountedRef.current) return;
            playError();
            flashStatus("error");
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
        } finally {
            if (mountedRef.current) {
                setLoading(false);
                setScanValue("");
            }
        }
    };

    // Keep activeBay in sync with bays after refreshQueue (active bay may
    // have emptied — fall back to the first remaining bay).
    useEffect(() => {
        if (!initialised) return;
        if (bays.length === 0) {
            setActiveBay(null);
            return;
        }
        if (!activeBay || !bays.find(b => b.location_id === activeBay.location_id)) {
            setActiveBay(bays[0]);
        }
    }, [bays, activeBay, initialised]);

    // Stock type of the pallet in hand — looked up in the queue so the
    // operator can see at a glance whether they've grabbed the envelopes
    // or the pallets from a mixed lane.
    const currentPalletType = currentPallet
        ? bays.flatMap(b => b.pallets).find(p => p.pallet_id === currentPallet)?.stock_type ?? null
        : null;

    const commandText = (() => {
        switch (phase) {
            case "suggest-bay":       return t("move_scan_pallet");
            case "awaiting-pallet":   return t("move_scan_pallet");
            case "awaiting-scansheet":return t("move_scan_scansheet");
            case "complete":          return t("move_all_caught_up");
        }
    })();
    const commandWords = commandText.toUpperCase().split(/[\s-]+/).filter(Boolean);

    const totalPlaced = bays.reduce((acc, b) => acc + b.count, 0);

    return (
        <div
            className="scan-panel move-panel"
            onMouseDown={preventScanPageButtonFocus}
            onClick={blurClickedScanPageButton}
        >
                <ScanFlashOverlay active={scanStatus === "error"} />
            <div className="page-title-bar">
                <h1 className="page-title">{t("move")}</h1>
                <button className="page-back-btn" onClick={() => navigate("/goods-in")}>
                    <ArrowLeft size={20} />
                </button>
            </div>

            {!initialised && (
                <div className="move-initial-loader">
                    <FlowLoader />
                </div>
            )}

            {initialised && phase !== "complete" && (
                <div className="book-in-stats-bar">
                    {currentPallet ? (
                        <span className="book-in-stat">
                            <Package size={16} />
                            <strong>{currentPallet}</strong>
                            {currentPalletType && (
                                <span className="move-type-tag">{currentPalletType}</span>
                            )}
                        </span>
                    ) : activeBay ? (
                        <span className="book-in-stat">
                            <MapPin size={16} />
                            <strong>{activeBay.display_name || activeBay.location_id}</strong>
                        </span>
                    ) : (
                        <span className="book-in-stat">
                            <MapPin size={16} />
                            <strong>{t("move_pallets_to_move", { count: String(totalPlaced) })}</strong>
                        </span>
                    )}
                    <span className="book-in-stat book-in-stat--count">
                        {t("move_pallets_to_move", { count: String(totalPlaced) })}
                    </span>
                </div>
            )}

            {initialised && (
                <div className="scan-centre">
                    <div className="scan-command-wrap">
                        <ScanStatusIcons status={scanStatus === "error" ? "idle" : scanStatus} />
                        <div className={`scan-command ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                            {commandWords.map((word, i) => (
                                <FitWord key={i}>{word}</FitWord>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {initialised && phase === "suggest-bay" && activeBay && (
                <div className="move-suggestion">
                    <div className="move-suggestion-card">
                        <div className="move-suggestion-prelabel">{t("move_go_to_lane")}</div>
                        <div className="move-suggestion-label">{activeBay.display_name || activeBay.location_id}</div>
                        <div className="move-suggestion-meta">
                            {t("move_pallets_in_bay", { count: String(activeBay.count) })}
                        </div>
                    </div>
                    {bays.length > 1 && (
                        <button
                            type="button"
                            className="book-in-command-action-btn"
                            onClick={() => setChooseLaneOpen(true)}
                        >
                            {t("move_choose_another_bay")}
                        </button>
                    )}
                </div>
            )}

            {/* Choose-lane override modal — listed in oldest-first order
                so the manual pick still nudges the operator toward the
                FIFO recommendation. The active lane is highlighted but
                still selectable (it's a no-op selection — just closes
                the modal). */}
            {chooseLaneOpen && (
                <div className="modal-overlay book-in-modal-overlay move-modal-overlay" onClick={() => setChooseLaneOpen(false)}>
                    <div className="modal-card move-choose-lane-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{t("move_choose_another_bay")}</h2>
                            <button className="modal-close" onClick={() => setChooseLaneOpen(false)}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <div className="move-bay-list">
                                {bays.map(b => {
                                    const isActive = activeBay?.location_id === b.location_id;
                                    return (
                                        <button
                                            key={b.location_id}
                                            type="button"
                                            className={`move-bay-row${isActive ? " move-bay-row--active" : ""}`}
                                            onClick={async () => {
                                                setActiveBay(b);
                                                setChooseLaneOpen(false);
                                                // Persist the override so other operators
                                                // see this lane as the recommendation. BE
                                                // clears it lazily once the lane is empty.
                                                try {
                                                    await apiFetch(`/pallet-receiving/move/active-lane`, {
                                                        method: "POST",
                                                        headers: { "Content-Type": "application/json" },
                                                        body: JSON.stringify({
                                                            warehouse: userObject.warehouse,
                                                            locationId: b.location_id,
                                                        }),
                                                    });
                                                    // Refetch so the queue reflects the new
                                                    // ordering (active lane first).
                                                    refreshQueue(false);
                                                } catch {
                                                    // Override-set is best-effort — if it
                                                    // fails the local FE still tracks the
                                                    // pick, just doesn't share it across
                                                    // operators.
                                                }
                                            }}
                                        >
                                            <span className="move-bay-name">{b.display_name || b.location_id}</span>
                                            <span className="move-bay-count">
                                                {t("move_pallets_in_bay", { count: String(b.count) })}
                                            </span>
                                            <ChevronRight size={20} />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {initialised && !inboundBarcode && phase !== "complete" && (
                <div className="move-no-inbound-banner">
                    <Truck size={16} />
                    <span>{t("move_no_inbound_location")}</span>
                </div>
            )}

            <HiddenScanInput
                value={scanValue}
                onChange={setScanValue}
                onSubmit={doSubmit}
                disabled={loading || phase === "complete"}
            />

            {/* Demo — toggled via Admin Settings → Demo Mode. All buttons
                are backed by real DB rows: placed pallet IDs come from the
                Move queue, the destination barcode is the warehouse's
                inbound-processing location row. */}
            {demo && initialised && phase !== "complete" && (
                <div className="book-in-demo-panel">
                    <span className="book-in-demo-label">Demo</span>
                    {(phase === "suggest-bay" || phase === "awaiting-pallet") && (
                        bays.flatMap(b => b.pallets).slice(0, 4).map(p => (
                            <button
                                key={p.pallet_id}
                                className="book-in-demo-btn"
                                onClick={() => doSubmit(p.pallet_id)}
                            >
                                {p.pallet_id}
                            </button>
                        ))
                    )}
                    {phase === "awaiting-scansheet" && (
                        <>
                            {inboundBarcode && (
                                <button
                                    className="book-in-demo-btn book-in-demo-btn--tote"
                                    onClick={() => doSubmit(inboundBarcode)}
                                >
                                    {inboundBarcode}
                                </button>
                            )}
                            {currentPallet && (
                                <button
                                    className="book-in-demo-btn"
                                    onClick={() => doSubmit(currentPallet)}
                                >
                                    Cancel {currentPallet}
                                </button>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default Move;
